import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import { Button } from "react-bootstrap";
import GoogleLogo from "@/images/google-logo.svg";
import FbLoginBannerDesktop from "@/images/fb-login-banner-desktop.png";
import passwordIcon from "@/images/password-toggle-icon.svg";
import passwordClosedIcon from "@/images/password-toggle-icon-closed.svg";
import {
  emailValidation,
  passwordValidation,
} from "@/helpers/validations";
import TextBox from "@/components/Common/TextBox";
import { LOGIN } from "@/constants/onBoarding";
import _ from "lodash";
import useIsMobile from "@/hooks/useIsMobile";
import { userSignUp, verifyEmail } from "@/services/v2/signupService";
import { updateUserDetailsToStorage } from "@/helpers/authHelpers";
import { useOAuthLogin } from "@/hooks/useOAuthLogin";
import { useCurrentWebsite } from "@/hooks/useCurrentWebsite";
import useLoading from "@/hooks/useLoading";

const LOADING_KEY = "signup";

const SHOW_GOOGLE_LOGIN = false;

const SignupModal = (props) => {

  const {
    show,
    onHide,
    onLoginSuccess,
    onSignupComplete,
    agentOnboardingPopup,
    handleAgentOnboardingModal,
  } = props;

  const { startLoading, stopLoading } = useLoading();
  const isMobile = useIsMobile();

  const utm_source = _.get(agentOnboardingPopup, "params.utm_source", "");
  const utm_medium = _.get(agentOnboardingPopup, "params.utm_medium", "");
  const utm_campaign = _.get(agentOnboardingPopup, "params.utm_campaign", "");

  const { isGitchaDefaultWebsite, mls } = useCurrentWebsite();

  const initialFormData = {
    email: "",
    pwd: "",
    confPwd: "",
  };
  const initialErrors = {
    emailError: "",
    pwdError: "",
    confPwdError: "",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [formErrors, setFormErrors] = useState(initialErrors);
  const [isShowPassword, setPasswordShow] = useState(false);
  const [confirmPasswordShow, setConfirmPasswordShow] = useState(false);

  const onTextChange = (e) => {
    const value = e.target.value;
    setFormData((formData) => ({ ...formData, [e.target.name]: value }));
  };

  const email = _.get(formData, "email", null);
  const pwd = _.get(formData, "pwd", null);
  const confPwd = _.get(formData, "confPwd", null);

  const buttonDisable = !(email && pwd && confPwd);

  const handleOAuthSuccess = (response, provider) => {
    stopLoading(LOADING_KEY);
    const isFirstTimeSigningUp = response?.result?.isFirstTimeSigningUp;

    updateUserDetailsToStorage(response, provider);
    handleCloseModal?.();

    if (isFirstTimeSigningUp) return onSignupComplete?.();

    onLoginSuccess?.();
  };

  const handleOAuthLoginWithLoading = (provider) => {
    startLoading(LOADING_KEY);
    handleOAuthLogin(provider);
  };

  const { handleOAuthLogin } = useOAuthLogin({ onAuthSuccess: handleOAuthSuccess });

  const checkEmailExist = async (email) => {
    const userEmail = { Email: email };
    return await verifyEmail(userEmail).then((response) => {
      return response?.result;
    });
  };

  const validate = async () => {
    setFormErrors(initialErrors);
    let isValid = true;
    const email = _.get(formData, "email", null).trim();
    const pwd = _.get(formData, "pwd", null).trim();
    const confPwd = _.get(formData, "confPwd", null).trim();
    const passWordError = passwordValidation(pwd);

    if (!emailValidation(email)) {
      setFormErrors((formErrors) => ({
        ...formErrors,
        emailError: "Invalid Email",
      }));
      isValid = false;
    } else if (passWordError) {
      setFormErrors((formErrors) => ({
        ...formErrors,
        pwdError: passWordError,
      }));
      isValid = false;
    } else {
      const res = await checkEmailExist(email);
      const userExist = _.get(res, "UserExists", false);

      if (userExist) {
        setFormErrors((formErrors) => ({
          ...formErrors,
          emailError: "Email already exists",
        }));
        isValid = false;
      } else if (pwd !== confPwd) {
        setFormErrors((formErrors) => ({
          ...formErrors,
          confPwdError: "Password not matching",
        }));
        isValid = false;
      } else {
        isValid = true;
      }
    }

    return isValid;
  };

  const handleSubmit = async () => {
    startLoading(LOADING_KEY);
    try {
      const isValid = await validate();

      if (isValid) {
        const reqPayload = {
          email: _.get(formData, "email", ""),
          password: _.get(formData, "pwd", ""),
          confirmPassword: _.get(formData, "confPwd", ""),
          utmSource: utm_source,
          utcMedium: utm_medium,
          utcCampaign: utm_campaign,
          mls,
        };

        await userSignUp(reqPayload).then((response) => {
          if (_.get(response, "errorCode", "") === 0) {

            updateUserDetailsToStorage(response, null, null, mls);

            handleCloseModal?.();
            onSignupComplete?.();

          } else {
            setFormErrors((formErrors) => ({
              ...formErrors,
              emailError: _.get(response, "message", ""),
            }));
          }
        });
      }
    } finally {
      stopLoading(LOADING_KEY);
    }
  };

  const handleClearFormState = () => {
    setFormData(initialFormData);
    setFormErrors(initialErrors);
    setPasswordShow(false);
    setConfirmPasswordShow(false);
  }

  const handleCloseModal = () => {
    handleClearFormState?.()
    onHide?.();
  };

  useEffect(() => {
    if (!show) {
      handleClearFormState();
    }
  }, [show]);

  const handleToggleToLogin = () => {
    handleAgentOnboardingModal?.({ showPopup: true, userAction: LOGIN });
  };

  if (!isGitchaDefaultWebsite) {
    return null
  }

  return (
    <Modal
      show={show}
      size="md"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      fullscreen={isMobile}
      backdrop="static"
      className={isMobile ? '' : 'modal-width-548'}
      onHide={handleCloseModal}
    >
      <Modal.Body className="signup-modal-main" style={isMobile ? { justifyContent: 'center', display: 'flex', flexDirection: 'column' } : {}}>
        <button
          type="button"
          className="btn-close"
          aria-label="Close"
          onClick={handleCloseModal}
        ></button>

        <div className="d-flex align-items-center justify-content-between">
          <h2 className="mob-style-heading text-center mb-0 signup-modal-heading m-auto">
            Welcome to Gitcha!
          </h2>
        </div>

        <div className="fb-login-banner-desktop mt-18">
          <img src={FbLoginBannerDesktop} alt="Login Banner" />
        </div>

        <div className="fb-login-tagline-desktop">
          Create your account to get started, it's free!
        </div>

        <div className="textbox-wrap mb-2 mb-sm-2 position-relative">
          <label className="form-label">Email</label>
          <TextBox
            name="email"
            type="text"
            tabIndex="1"
            autoComplete="email"
            autoFocus={true}
            errorMessage={_.get(formErrors, "emailError", "")}
            value={_.get(formData, "email", "")}
            placeholder="yourname@email.com"
            className={
              _.get(formErrors, "emailError", null)
                ? "form-control validation-txtfld"
                : "form-control"
            }
            onChange={(e) => {
              onTextChange(e);
            }}
          />
        </div>

        <div
          className="textbox-wrap position-relative"
          style={{ marginBottom: "24px" }}
        >
          <label className="form-label">Password</label>
          <TextBox
            name="pwd"
            type={isShowPassword ? "text" : "password"}
            tabIndex="2"
            autoComplete="new-password"
            errorMessage={_.get(formErrors, "pwdError", "")}
            value={_.get(formData, "pwd", "")}
            placeholder="Create a Password"
            errorClass="error-msg height-inherit"
            className={
              _.get(formErrors, "pwdError", null)
                ? "pad-right-40 validation-txtfld"
                : "pad-right-40"
            }
            onChange={(e) => {
              onTextChange(e);
            }}
          />

          <Button
            variant=""
            className="btn btn-pwd-toggle"
            onClick={() => setPasswordShow((state) => !state)}
          >
            <img
              src={isShowPassword ? passwordIcon : passwordClosedIcon}
              alt="pwdicon"
            />
          </Button>
        </div>

        <div className="textbox-wrap mb-3 position-relative">
          <label className="form-label">Confirm Password</label>
          <TextBox
            name="confPwd"
            type={confirmPasswordShow ? "text" : "password"}
            tabIndex="3"
            autoComplete="new-password"
            errorMessage={_.get(formErrors, "confPwdError", "")}
            value={_.get(formData, "confPwd", "")}
            placeholder="Confirm Password"
            className={
              _.get(formErrors, "confPwdError", null)
                ? "form-control validation-txtfld"
                : "form-control"
            }
            onChange={(e) => {
              onTextChange(e);
            }}
          />
          <Button
            variant=""
            className="btn btn-pwd-toggle"
            onClick={() => setConfirmPasswordShow((state) => !state)}
          >
            <img
              src={confirmPasswordShow ? passwordIcon : passwordClosedIcon}
              alt="pwdicon"
            />
          </Button>
        </div>

        <Button
          variant="primary"
          tabIndex="4"
          className="w-100 mb-3"
          disabled={buttonDisable}
          onClick={() => handleSubmit()}
        >
          Continue
        </Button>

        {SHOW_GOOGLE_LOGIN && (
          <>
            <div className="segment-divider mb-16">OR</div>

            <Button
              variant="outline-primary"
              className="btn outline-btn w-100 mb-16"
              onClick={() => handleOAuthLoginWithLoading("google")}
            >
              <img src={GoogleLogo} alt="Google Login" className="me-2" />
              <span>Continue with Google</span>
            </Button>
          </>
        )}

        <p className="text-center mb-2">
          By signing up, you agree to Gitcha's
          <a role="button" target="_blank" href="terms-of-service">
            {" "}
            Terms
          </a>{" "}
          and
          <a role="button" target="_blank" href="privacy-policy">
            {" "}
            Privacy Policy.
          </a>
        </p>

        <p className="text-center">
          Already have an account?{" "}
          <a href={void 0} role="button" onClick={handleToggleToLogin}>
            Login here
          </a>
        </p>
      </Modal.Body>
    </Modal>
  );
};

export default SignupModal;
